<template>
  <div class="open-access-page" v-loading="loading">
    <!-- Hero Banner with background image -->
    <div class="oa-hero">
      <div class="oa-hero-inner">
        <div class="oa-hero-card">
          <p class="oa-hero-title">{{ hotTopicIntro }}</p>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="oa-content">
      <div class="oa-inner">
        <div class="oa-layout">
          <!-- Left Sidebar -->
          <div class="sidebar">
            <div class="sidebar-group">
              <h3>{{ $t("openAccess.topicCategory") }}</h3>
              <el-checkbox-group
                v-if="topicTypes.length"
                v-model="selectedTopics"
                class="filter-checkboxes"
                @change="onFilterChange"
              >
                <el-checkbox
                  v-for="topic in topicTypes"
                  :key="topic.typeId"
                  :label="topic.typeId"
                >
                  {{ topic.typeName }}
                </el-checkbox>
              </el-checkbox-group>
              <div v-else-if="!loading" class="empty-tip">
                {{ $t("common.empty") }}
              </div>
            </div>
            <div class="sidebar-group">
              <h3>{{ $t("openAccess.disciplineCategory") }}</h3>
              <el-checkbox-group
                v-if="disciplinaryFields.length"
                v-model="selectedDisciplines"
                class="filter-checkboxes"
                @change="onFilterChange"
              >
                <el-checkbox
                  v-for="(disc, index) in disciplinaryFields"
                  :key="index"
                  :label="disc"
                >
                  {{ disc }}
                </el-checkbox>
              </el-checkbox-group>
              <div v-else-if="!loading" class="empty-tip">
                {{ $t("common.empty") }}
              </div>
            </div>
          </div>

          <!-- Right Content -->
          <div class="main-content" v-loading="articleLoading">
            <!-- Category Header Card (full width) -->
            <div class="category-header">
              <div class="category-main">
                <div class="category-icon-wrapper">
                  <span class="category-icon-deco"></span>
                  <img
                    v-if="hotTopicIconSrc"
                    :src="hotTopicIconSrc"
                    alt=""
                    class="category-icon"
                    @error="onHotTopicIconError"
                  />
                  <div v-else class="category-icon category-icon-placeholder">
                    <img src="@/assets/images/open-icon.png" alt="" />
                  </div>
                </div>
                <div class="category-info">
                  <p class="hot-topic-label">{{ $t("openAccess.hotTopicLabel") }}</p>
                  <h2>
                    <img src="@/assets/images/open-icon.png" alt="" class="name-icon" />
                    {{ activeCategoryName }}
                  </h2>
                  <p class="hot-topic-value">{{ hotTopicDesc }}</p>
                </div>
              </div>
            </div>

            <!-- Stats & Sort -->
            <div class="sort-bar">
              <span class="stats-text">{{ $t("openAccess.totalArticles", { count: total }) }}</span>
              <div class="sort-right">
                <span class="sort-label">{{ $t("explore.sortBy") }}</span>
                <span
                  v-if="showRelevance"
                  :class="['sort-option', { 'is-active': sortField === 'relevance' }]"
                  @click="onSortFieldChange('relevance')"
                >
                  {{ $t("openAccess.list.sortRelevance") }}
                </span>
                <span
                  :class="['sort-option', { 'is-active': sortField === 'publishDate' }]"
                  @click="onSortFieldChange('publishDate')"
                >
                  {{ $t("openAccess.list.sortPublicationDate") }}
                  <i
                    v-if="sortField === 'publishDate'"
                    :class="orderType === 1 ? 'el-icon-top' : 'el-icon-bottom'"
                  ></i>
                </span>
              </div>
            </div>

            <!-- Article List / Empty -->
            <div class="article-list">
              <template v-if="articleList.length">
                <div
                  v-for="article in articleList"
                  :key="article.id"
                  class="article-item"
                  @click="goToArticleDetail(article)"
                >
                  <div class="article-main">
                    <div class="article-cover">
                      <img v-if="article.cover" :src="article.cover" :alt="article.title" />
                      <img v-else src="@/assets/images/no-img.png" alt="no image" />
                    </div>
                    <div class="article-info">
                      <h3 class="article-title">{{ article.title }}</h3>
                      <div class="article-meta-list">
                        <div class="meta-row" v-if="article.topic">
                          <span class="meta-label">{{ $t("openAccess.list.researchTopic") }}</span>
                          <span class="meta-value">{{ article.topic }}</span>
                        </div>
                        <div class="meta-row" v-if="article.researcher">
                          <span class="meta-label">{{ $t("openAccess.list.researcher") }}</span>
                          <span class="meta-value">{{ article.researcher }}</span>
                        </div>
                        <div class="meta-row" v-if="article.disciplinaryField">
                          <span class="meta-label">{{ $t("openAccess.list.disciplinaryField") }}</span>
                          <span class="meta-value">{{ article.disciplinaryField }}</span>
                        </div>
                        <div class="meta-row" v-if="article.publishDate">
                          <span class="meta-label">{{ $t("openAccess.list.publicationDate") }}</span>
                          <span class="meta-value">{{ article.publishDate }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <p class="article-abstract" v-if="article.abstract">
                    <span class="abstract-label">{{ $t("openAccess.list.abstract") }}</span>
                    {{ article.abstract }}
                  </p>
                </div>
              </template>
              <el-empty
                v-else-if="!articleLoading"
                :description="$t('common.empty')"
              ></el-empty>
            </div>

            <!-- Pagination -->
            <div v-if="total" class="pagination-wrapper">
              <el-pagination
                background
                layout="prev, pager, next, sizes"
                :total="total"
                :page-size.sync="pageSize"
                :page-sizes="pageSizes"
                :current-page.sync="currentPage"
                @current-change="handlePageChange"
                @size-change="handleSizeChange"
              ></el-pagination>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import defaultHotTopicIcon from "@/assets/images/open-ai.png";
import { getImage } from "@/utils/tools";
import {
  getHotTopic,
  listTopicType,
  listDisciplinaryField,
  listArticlePage,
} from "@/api/article";

export default {
  name: "OpenAccessPage",
  data() {
    return {
      loading: false,
      articleLoading: false,
      hotTopic: {},
      topicTypes: [],
      disciplinaryFields: [],
      selectedTopics: [],
      selectedDisciplines: [],
      sortField: "publishDate",
      orderType: 2,
      currentPage: 1,
      pageSize: 5,
      pageSizes: [5, 10, 15, 20],
      total: 0,
      articleList: [],
    };
  },
  computed: {
    hotTopicIconSrc() {
      const addr = this.hotTopic && this.hotTopic.hotTypeImageAddr;
      if (addr) return getImage(addr);
      return defaultHotTopicIcon || "";
    },
    hotTopicDesc() {
      return (this.hotTopic && this.hotTopic.hotTypeDescription) || "";
    },
    hotTopicIntro() {
      return (this.hotTopic && this.hotTopic.articleIntroduction) || "";
    },
    activeCategoryName() {
      return (this.hotTopic && this.hotTopic.typeName) || "";
    },
    showRelevance() {
      return (
        this.selectedTopics.length > 0 || this.selectedDisciplines.length > 0
      );
    },
  },
  created() {
    this.loadFilters();
  },
  watch: {
    showRelevance(val) {
      if (!val && this.sortField === "relevance") {
        this.sortField = "publishDate";
        this.orderType = 2;
      }
    },
  },
  methods: {
    async loadFilters() {
      this.loading = true;
      try {
        const [hot, topics, disciplines] = await Promise.all([
          getHotTopic(),
          listTopicType(),
          listDisciplinaryField(),
        ]);
        this.hotTopic = hot?.data || {};
        this.topicTypes = Array.isArray(topics?.data) ? topics.data : [];
        const fieldList = Array.isArray(disciplines?.data) ? disciplines.data : [];
        this.disciplinaryFields = [
          ...new Set(
            fieldList
              .map((item) => item && item.disciplinaryField)
              .filter(Boolean)
          ),
        ];
        const hotTypeId = this.hotTopic.hotTypeId;
        if (
          hotTypeId != null &&
          this.topicTypes.some((t) => t.typeId === hotTypeId)
        ) {
          this.selectedTopics = [hotTypeId];
        }
      } catch (err) {
        this.hotTopic = {};
        this.topicTypes = [];
        this.disciplinaryFields = [];
      } finally {
        this.loading = false;
      }
      this.loadArticles();
    },
    async loadArticles() {
      this.articleLoading = true;
      try {
        const payload = {
          pageNum: this.currentPage,
          pageSize: this.pageSize,
          typeIdList: this.selectedTopics,
          disciplinaryFieldList: this.selectedDisciplines,
          orderField: this.sortField === "relevance" ? 1 : 2,
          orderType: this.orderType,
          searchValue: "",
        };
        const res = await listArticlePage(payload);
        const rows = Array.isArray(res?.rows) ? res.rows : [];
        this.articleList = rows.map((item) => ({
          id: item.articleId,
          title: item.articleName,
          topic: item.topic,
          researcher: item.researcher,
          disciplinaryField: item.disciplinaryField,
          publishDate: item.year,
          abstract: item.synopsis,
          cover: item.articleCoverAddr ? getImage(item.articleCoverAddr) : null,
        }));
        this.total = Number(res?.total) || 0;
      } catch (err) {
        this.articleList = [];
        this.total = 0;
      } finally {
        this.articleLoading = false;
      }
    },
    onHotTopicIconError(e) {
      if (defaultHotTopicIcon && e.target.src !== defaultHotTopicIcon) {
        e.target.src = defaultHotTopicIcon;
      } else {
        e.target.style.display = "none";
      }
    },
    onSortFieldChange(field) {
      if (this.sortField === field) {
        this.orderType = this.orderType === 2 ? 1 : 2;
      } else {
        this.sortField = field;
        this.orderType = 2;
      }
      this.currentPage = 1;
      this.loadArticles();
    },
    onFilterChange() {
      this.currentPage = 1;
      this.loadArticles();
    },
    handlePageChange() {
      this.loadArticles();
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    handleSizeChange() {
      this.currentPage = 1;
      this.loadArticles();
    },
    goToArticleDetail(article) {
      this.$router.push({ path: `/article/${article.id}/${article.title}` });
    },
  },
};
</script>

<style lang="scss" scoped>
.open-access-page {
  flex: 1;
}

/* ===== Hero Banner ===== */
.oa-hero {
  background: url("~@/assets/images/open-top.png") center / cover no-repeat;
  padding: 36px 0;
}

.oa-hero-inner {
  max-width: $max-width;
  margin: 0 auto;
  height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.oa-hero-card {
  width: 1000px;
  height: 300px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 0 16px;
  box-sizing: border-box;
}

.oa-hero-title {
  font-size: 28px;
  line-height: 1.5;
  color: $text-dark;
  white-space: pre-line;
}

/* ===== Content ===== */
.oa-content {
  padding: 24px 0 48px;
}

.oa-inner {
  max-width: $max-width;
  margin: 0 auto;
}

/* ===== Category Header Card ===== */
.category-header {
  margin-bottom: 24px;
  position: sticky;
  top: 0;
  z-index: 10;
  background: $white;
  padding-top: 12px;
}

.category-left {
  flex-shrink: 0;
  background: $bg-light;
  padding: 20px 12px;
  align-self: stretch;
  display: flex;
  align-items: center;

  .category-label {
    font-size: 16px;
    color: $text-light;
    writing-mode: vertical-rl;
    letter-spacing: 4px;
  }
}

.category-main {
  display: flex;
  align-items: center;
  gap: 24px;
  padding: 20px 0;

  .category-icon-wrapper {
    position: relative;
    flex-shrink: 0;
    width: 110px;
    height: 110px;
  }

  .category-icon-deco {
    position: absolute;
    left: -5px;
    bottom: -5px;
    width: 110px;
    height: 110px;
    border-radius: 50%;
    background: $primary-orange;
    z-index: 0;
  }

  .category-icon {
    position: relative;
    z-index: 1;
    width: 110px;
    height: 110px;
    border-radius: 50%;
    object-fit: cover;
    display: block;
    background: $white;
  }

  .category-icon-placeholder {
    display: flex;
    align-items: center;
    justify-content: center;
    background: $gradient-orange;

    img {
      width: 44px;
      height: 44px;
      filter: brightness(0) invert(1);
    }
  }

  .category-info {
    .hot-topic-label {
      font-size: 16px;
      color: $text-gray;
      margin-bottom: 10px;
    }
    h2 {
      display: flex;
      align-items: center;
      gap: 4px;
      font-size: 20px;
      color: $text-dark;
      margin-bottom: 14px;

      .name-icon {
        width: 22px;
        height: 22px;
      }
    }

    .hot-topic-value {
      font-size: 16px;
      color: $text-light;
      line-height: 1.5;
    }
  }
}

/* ===== Layout ===== */
.oa-layout {
  display: flex;
  gap: 20px;
}

/* ===== Sidebar ===== */
.sidebar {
  flex-shrink: 0;
  width: 260px;
  padding: 12px 0;
  position: sticky;
  top: 20px;
  align-self: flex-start;
}

.sidebar-group {
  margin-bottom: 20px;

  h3 {
    font-size: 18px;
    color: $text-dark;
    margin-bottom: 8px;
  }

  .empty-tip {
    color: $text-gray;
    font-size: 14px;
    padding: 6px 0;
  }
}

.filter-checkboxes {
  display: flex;
  flex-direction: column;

  ::v-deep .el-checkbox {
    margin-right: 0;
    margin-left: 0;
    padding: 6px 0;
    font-size: 16px;
    color: $text-gray;

    .el-checkbox__label {
      font-size: 16px;
      color: $text-gray;
      padding-left: 8px;
    }

    .el-checkbox__input.is-checked + .el-checkbox__label {
      color: $primary-orange;
    }
  }
}

/* ===== Main Content ===== */
.main-content {
  flex: 1;
  min-width: 0;
}

.sort-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 20px;
  font-size: 16px;
  color: $text-gray;

  .stats-text {
    color: $text-gray;
  }

  .sort-right {
    display: inline-flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
  }

  .sort-label {
    color: $text-gray;
    font-size: 16px;
  }

  .sort-option {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 12px;
    font-size: 16px;
    color: $text-gray;
    border: 1px solid transparent;
    border-radius: 14px;
    cursor: pointer;
    user-select: none;
    transition: all 0.2s;

    i {
      font-weight: 700;
    }

    &:hover {
      color: $primary-orange;
    }

    &.is-active {
      background: $bg-orange-light;
      border-color: $primary-orange;
      color: $primary-orange;
    }
  }
}

/* ===== Article List ===== */
.article-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-height: 200px;
}

.article-item {
  padding: 20px 16px;
  border-bottom: 1px solid $border-color;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: $bg-light;

    .article-title {
      text-decoration: underline;
    }
  }
}

.article-main {
  display: flex;
  gap: 20px;
  margin-bottom: 10px;
}

.article-cover {
  flex-shrink: 0;
  width: 120px;

  img {
    width: 100%;
    border-radius: 4px;
    display: block;
  }
}

.article-info {
  flex: 1;
  min-width: 0;

  .article-title {
    font-size: 16px;
    font-weight: 700;
    color: $primary-orange;
    margin-bottom: 10px;
    line-height: 1.4;
  }
}

.article-meta-list {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 4px 8px;

  .meta-row {
    display: contents;
  }

  .meta-label {
    color: $text-gray;
    text-align: left;
  }

  .meta-value {
    color: $text-dark;
  }
}

.article-keywords {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 8px;
  font-size: 16px;

  .kw-label {
    color: $text-gray;
    flex-shrink: 0;
  }

  .kw-item {
    color: $primary-orange;
  }
}

.article-abstract {
  font-size: 16px;
  line-height: 1.7;
  color: $text-dark;

  .abstract-label {
    color: $text-gray;
    margin-right: 4px;
  }
}

/* ===== Pagination ===== */
.pagination-wrapper {
  display: flex;
  justify-content: center;
  margin-top: 24px;

  ::v-deep .el-pagination.is-background {
    .btn-prev,
    .btn-next {
      background: transparent;
      color: $text-gray;

      &:hover {
        color: $primary-orange;
      }
    }

    .el-pager li {
      background: transparent;
      color: $text-gray;
      font-weight: 400;

      &:hover {
        color: $primary-orange;
      }

      &.is-active {
        background-color: $primary-orange;
        color: $white;
        border-radius: 4px;
      }
    }
  }
}
</style>
